import {
  ArrowUpRight,
  CheckCircle2,
  Clock3,
  ExternalLink,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/utils/cn';
import { useT } from '@/utils/i18n';
import type { I18nKey } from '@/generated/i18n-keys';

export interface BookSharePayload {
  kind: 'book_share';
  action?: 'share' | string;
  code?: string;
  message?: string;
  source_url?: string;
  status?: string;
  public_url?: string;
  submission_id?: string;
  is_dedup?: boolean;
}

function stripJSONFence(text: string): string {
  const trimmed = text.trim();
  const fenced = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  return fenced ? fenced[1].trim() : trimmed;
}

export function tryParseBookSharePayload(
  jsonText: string
): BookSharePayload | null {
  try {
    const parsed = JSON.parse(stripJSONFence(jsonText));
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      (parsed as { kind?: unknown }).kind === 'book_share'
    ) {
      return parsed as BookSharePayload;
    }
  } catch {
    // not a Book payload
  }
  return null;
}

function hostLabel(rawURL?: string): string | null {
  if (!rawURL) return null;
  try {
    return new URL(rawURL).host;
  } catch {
    return rawURL;
  }
}

async function openURL(url: string) {
  const utilsBridge = window.jsb?.UtilsBridge;
  if (utilsBridge?.openURL) {
    await utilsBridge.openURL(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

async function copyURL(url: string) {
  await navigator.clipboard.writeText(url);
}

// Server-defined `BookSharePayload.code` values, kept in lockstep with
// BookSkillExecutor.ResultCode (frontend/macos/Cueboard/Backend/Skills/
// BookSkillExecutor.swift:43-50). New codes added on the backend must also
// land here so the card doesn't silently render them as "Generating".
const ERROR_CODES = new Set([
  'share_invalid_url',
  'share_ownership_mismatch',
  'share_rejected',
  'internal_error',
]);

type CardVariant = 'success' | 'error' | 'pending';

const VARIANT: Record<
  CardVariant,
  {
    label: I18nKey;
    title: I18nKey;
    icon: typeof CheckCircle2;
    iconClass: string;
    pillClass: string;
    defaultSubtitle: I18nKey;
  }
> = {
  success: {
    label: 'Published',
    title: 'Published to Book',
    icon: CheckCircle2,
    iconClass: 'bg-emerald-500/12 text-emerald-500',
    pillClass: 'bg-emerald-500/12 text-emerald-500',
    defaultSubtitle: 'Your shared site is published in Book.',
  },
  error: {
    label: 'Needs attention',
    title: 'Could not publish to Book',
    icon: XCircle,
    iconClass: 'bg-error-fg/12 text-error-fg',
    pillClass: 'bg-error-fg/12 text-error-fg',
    defaultSubtitle: 'Book could not publish this site.',
  },
  pending: {
    label: 'Preparing preview',
    title: 'Publishing to Book',
    icon: Clock3,
    iconClass: 'bg-amber-500/12 text-amber-500',
    pillClass: 'bg-amber-500/12 text-amber-600',
    defaultSubtitle: 'Book is preparing the preview before publishing.',
  },
};

function pickVariant(payload: BookSharePayload): CardVariant {
  if (payload.code === 'shared' || payload.status === 'published')
    return 'success';
  if (payload.code && ERROR_CODES.has(payload.code)) return 'error';
  return 'pending';
}

export function BookShareCard({ payload }: { payload: BookSharePayload }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const publicURL = payload.public_url?.trim();
  const sourceURL = payload.source_url?.trim();
  const variant = pickVariant(payload);
  const v = VARIANT[variant];
  const StatusIcon = v.icon;
  const displayURL = publicURL || sourceURL || '';
  const subtitle = payload.message?.trim() || t(v.defaultSubtitle);
  const urlLabel = hostLabel(publicURL) ?? hostLabel(sourceURL);

  const handleCopy = async () => {
    if (!displayURL) return;
    try {
      await copyURL(displayURL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Keep the button available; the host may deny clipboard access.
    }
  };

  return (
    <div
      className={cn(
        'w-full max-w-[32.5rem] overflow-hidden rounded-xl',
        'border border-border/80 bg-surface-card/80 shadow-sm backdrop-blur'
      )}
    >
      <div className="flex gap-3 p-3">
        <div
          className={cn(
            'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full',
            v.iconClass
          )}
        >
          <StatusIcon className="size-4" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[0.8125rem] font-semibold leading-5 text-text-primary">
                {t(v.title)}
              </div>
              <div className="mt-0.5 text-xs leading-5 text-text-secondary">
                {subtitle}
              </div>
            </div>
            <div
              className={cn(
                'shrink-0 rounded-full px-2 py-0.5 text-[0.6875rem] font-medium leading-4',
                v.pillClass
              )}
            >
              {t(v.label)}
            </div>
          </div>

          {urlLabel ? (
            <div className="mt-2 truncate text-xs text-text-tertiary">
              {urlLabel}
            </div>
          ) : null}

          {displayURL ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {publicURL ? (
                <button
                  type="button"
                  onClick={() => {
                    openURL(publicURL).catch(() => undefined);
                  }}
                  className={cn(
                    'inline-flex h-7 items-center gap-1.5 rounded-md bg-fill-medium px-2.5 text-xs font-medium text-text-primary',
                    'transition-colors hover:bg-fill-strong'
                  )}
                >
                  <ExternalLink className="size-3.5" />
                  {t('Open')}
                </button>
              ) : null}
              {/* Owner management entry — routes to the Bridge Web `/book`
                  page where the user can upload preview media, hide /
                  restore / delete, or finish a publish. When we have a
                  submission id we deep-link to that item; otherwise just
                  drop into the management list (Peng + Cindy task #48 in
                  #bridge:bb4b360d). The Chat action only creates the
                  Book item — preview / publish loop happens in /book. */}
              <a
                href={
                  payload.submission_id
                    ? `/book?item=${encodeURIComponent(payload.submission_id)}`
                    : '/book'
                }
                className={cn(
                  'inline-flex h-7 items-center gap-1.5 rounded-md border border-border/80 px-2.5 text-xs font-medium text-text-primary',
                  'transition-colors hover:bg-fill-soft'
                )}
              >
                <ArrowUpRight className="size-3.5" />
                {variant === 'success' ? t('Manage in Book') : t('Add preview')}
              </a>
              <button
                type="button"
                onClick={handleCopy}
                className={cn(
                  'inline-flex h-7 items-center rounded-md border border-border/80 px-2.5 text-xs font-medium text-text-secondary',
                  'transition-colors hover:bg-fill-soft hover:text-text-primary'
                )}
              >
                {copied ? t('Copied') : t('Copy URL')}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
